/**
 * The operative's first name: asked for before the doors (WebSmName) and
 * engraved on their key after the first debrief (KeyEngraving). The texts
 * address them by it.
 */
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/shared/lib/api-client";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";
import { ensureSession } from "@/shared/lib/ensure-session";

/** Mirrors the server's rule (secretmissions save_name). */
export const NAME_MAX = 24;
export const NAME_RE = /^[\p{L}]+(?:[ '.-][\p{L}]+)*\.?$/u;

export const cleanName = (raw: string) => raw.replace(/\s+/g, " ").trimStart();

/** The name engraved last time, so a returning operative's key keeps it. A
 *  convenience only: the member's FirstName on the server is the record. */
const NAME_KEY = "secret-missions-key-name";
export const lastName = () => {
  try {
    return localStorage.getItem(NAME_KEY) ?? "";
  } catch {
    return "";
  }
};

/** The name, once it has been engraved on their key (not just given): the
 *  key's pictures show it from then on. '' until then. */
const ENGRAVED_KEY = "secret-missions-key-engraved";
export const engravedName = () => {
  try {
    return localStorage.getItem(ENGRAVED_KEY) ?? "";
  } catch {
    return "";
  }
};
export const markEngraved = (name: string) => {
  try {
    localStorage.setItem(ENGRAVED_KEY, name);
  } catch {
    /* private mode */
  }
};

/** Save their first name (the texts address them by it) and remember it here,
 *  so the key comes pre-engraved. Throws when the save fails. */
export async function saveName(name: string) {
  await ensureSession();
  await apiClient.post(API_ENDPOINTS.mission.name, { name });
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch {
    /* private mode — they just type it again next time */
  }
}

/** The name on their member (the session knows it even on a device that
 *  never saw them type it, e.g. one of our texted links). '' when none. */
export const useMemberName = () =>
  useQuery({
    queryKey: ["secretmissions", "session", "firstName"],
    staleTime: Infinity,
    queryFn: async () => {
      const { data } = await apiClient.post<{
        result?: { firstName?: string };
      }>(API_ENDPOINTS.session.ensure, {});
      return (data?.result?.firstName ?? "").trim();
    },
  });
